import {
  Build,
  BuildOutlined,
  ExpandMore,
  Explore,
  ExploreOutlined,
  Home,
  HomeOutlined,
  LocalOffer,
  LocalOfferOutlined,
  Settings,
  SettingsOutlined,
  TouchApp,
  TouchAppOutlined,
  Visibility,
  VisibilityOutlined,
  Work,
  WorkOutline,
} from "@mui/icons-material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/auth-context";
import { DiscoveryContext as BrandAuditDiscoveryContext } from "../../luminaire/BrandAudit/discovery-context";
import { DiscoveryContext as DiscoveryContextV2 } from "../../luminaire/DiscoveryV2/discovery-context";
import { DiscoveryContext } from "../../luminaire/Discovery/discovery-context";
import { CategoryAnalysisContext } from "../../luminaire/CategoryAnalysis/category-analysis-context";
import styles from "./SidebarV2.module.css";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import { isFlagOn } from "../../utils/navFlags";

/**
 * V2 of Sidebar.js (left untouched as the original flat nav).
 *
 * DIFFERENT FROM V1
 *  - Accordion groups (Discovery, Assessment/Audit, Conversations & Content Mapping,
 *    Monitoring, Settings); Home, Home Page - New and LLM Insights stay top-level links.
 *  - Discovery and Audit each have Brand / Product sub-options (Audit also Category,
 *    which is the Category Analysis page); active state follows the selected type.
 *  - "Audit" label comes from ENTITY_LABELS.audit (default "Assessment").
 *  - Bottom fade cue, scroll-into-view on expand, fluid spacing (SidebarV2.module.css).
 *  - Reporting item commented out; "Settings" renamed "General Settings".
 *
 * TO REVERT: in luminaire/Layout.js import "../Component/Sidebar/Sidebar" instead.
 */
const SidebarV2 = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const discoveryCtx = useContext(DiscoveryContext);
  const discoveryCtxV2 = useContext(DiscoveryContextV2);
  const brandAuditDiscoveryContext = useContext(BrandAuditDiscoveryContext);
  const categoryAnalysisCtx = useContext(CategoryAnalysisContext);
  const isAdmin =
    user?.user_role?.role?.name == "Company admin" ||
    user?.user_role?.role?.name == "Super User";

  // isFlagOn: dev shows these items without flags, prod needs them truthy - see utils/navFlags.js (STRICT_FLAGS).
  // Items without a `group` are top-level links; the rest render inside their group.
  const allItems = [
    {
      target: "home",
      label: "Home",
      icon: Home,
      iconOutlined: HomeOutlined,
      visible: user?.is_homescreen_enabled,
    },
    {
      target: "home-page-new",
      label: "Home Page - New",
      icon: Home,
      iconOutlined: HomeOutlined,
      visible: user?.is_new_homepage_enabled,
    },
    {
      target: "prompt-engine",
      label: "LLM Insights",
      icon: Build,
      iconOutlined: BuildOutlined,
      visible: true,
    },
    {
      id: "discovery-brand",
      group: "discovery",
      target: "discoveryV2",
      ctxKey: "discovery",
      type: "brand",
      label: ENTITY_LABELS.brand,
      visible: true,
    },
    {
      id: "discovery-product",
      group: "discovery",
      target: "discoveryV2",
      ctxKey: "discovery",
      type: "product",
      label: ENTITY_LABELS.product,
      visible: ENTITY_LABELS.hasProductEntity,
    },
    {
      id: "audit-brand",
      group: "audit",
      target: "brand-audit",
      ctxKey: "audit",
      type: "brand",
      label: ENTITY_LABELS.brand,
      visible: isFlagOn(user?.is_brandaudit_enabled),
    },
    {
      id: "audit-product",
      group: "audit",
      target: "brand-audit",
      ctxKey: "audit",
      type: "product",
      label: ENTITY_LABELS.product,
      visible: isFlagOn(user?.is_brandaudit_enabled) && ENTITY_LABELS.hasProductEntity,
    },
    {
      // Category Analysis page is the "Category" audit
      id: "audit-category",
      group: "audit",
      target: "category-analysis",
      label: "Category",
      visible: isFlagOn(user?.is_categoryanalysis_enabled),
    },
    {
      group: "conversations",
      target: "conversations",
      label: "Conversations",
      icon: TouchApp,
      iconOutlined: TouchAppOutlined,
      visible: isFlagOn(user?.is_conversation_enabled),
    },
    {
      group: "conversations",
      target: "metric-content",
      label: "Content Mapping",
      icon: TouchApp,
      iconOutlined: TouchAppOutlined,
      visible: isFlagOn(user?.is_contentmap_enabled),
    },
    // Reporting is not used currently
    // {
    //   group: "discovery",
    //   target: "llm-reporting",
    //   label: "Reporting",
    //   icon: ContentPasteGoOutlined,
    //   iconOutlined: ContentPasteGoOutlined,
    //   visible: user?.is_reporting_available,
    // },
    {
      group: "monitoring",
      target: "brand-monitoring",
      label: `${ENTITY_LABELS.brand} Analysis and Monitoring`,
      icon: LocalOffer,
      iconOutlined: LocalOfferOutlined,
      visible: isFlagOn(user?.is_monitoring_enable),
    },
    {
      group: "monitoring",
      target: "product-monitoring",
      label: `${ENTITY_LABELS.product} Analysis and Monitoring`,
      icon: Work,
      iconOutlined: WorkOutline,
      visible: isFlagOn(user?.is_monitoring_enable),
    },
    {
      group: "monitoring",
      target: "category-monitoring",
      label: "Category Monitoring",
      icon: Visibility,
      iconOutlined: VisibilityOutlined,
      visible: user?.is_category_monitoring_enabled,
    },
    {
      group: "monitoring",
      target: "actions",
      label: "Actions",
      icon: TouchApp,
      iconOutlined: TouchAppOutlined,
      visible: user?.is_action_enabled,
    },
    {
      group: "settings",
      target: "settings",
      label: "General Settings",
      icon: Settings,
      iconOutlined: SettingsOutlined,
      visible: isAdmin && isFlagOn(user?.is_setting_enable),
    },
    {
      group: "settings",
      target: "templateSetting",
      label: "Template Settings",
      icon: Settings,
      iconOutlined: SettingsOutlined,
      visible: isFlagOn(user?.is_template_setting_enabled),
    },
  ].filter((item) => item.visible);

  const groupDefs = [
    // alwaysGroup: keep the expandable header even when only one item is enabled
    { key: "discovery", label: "Discovery", icon: Explore, iconOutlined: ExploreOutlined, alwaysGroup: true },
    { key: "audit", label: ENTITY_LABELS.audit, icon: Work, iconOutlined: WorkOutline, alwaysGroup: true },
    { key: "conversations", label: "Conversations & Content Mapping", icon: TouchApp, iconOutlined: TouchAppOutlined },
    { key: "monitoring", label: "Monitoring", icon: Visibility, iconOutlined: VisibilityOutlined },
    { key: "settings", label: "Settings", icon: Settings, iconOutlined: SettingsOutlined },
  ];

  const topLevelItems = allItems.filter((item) => !item.group);
  const groups = groupDefs
    .map((def) => ({ ...def, items: allItems.filter((item) => item.group === def.key) }))
    .filter((g) => g.items.length > 0);

  const pathSegments = location.pathname.split("/");
  // Brand/Product share one route per page, so their active state also depends on the selected type.
  const selectedTypes = {
    discovery: discoveryCtxV2.type,
    audit: brandAuditDiscoveryContext.type,
  };
  const isTypedItemActive = (item) =>
    pathSegments.includes(item.target) && selectedTypes[item.ctxKey] === item.type;
  const isItemActive = (item) =>
    item.ctxKey ? isTypedItemActive(item) : pathSegments.includes(item.target);
  const activeGroupKey =
    allItems.find((item) => item.group && isItemActive(item))?.group ?? null;

  // Strict accordion: at most one group open; the group holding the current route opens on navigation.
  const [openGroup, setOpenGroup] = useState(activeGroupKey);
  useEffect(() => {
    if (activeGroupKey) setOpenGroup(activeGroupKey);
  }, [activeGroupKey]);

  // When a group opens, scroll it into view once its expand animation has finished.
  const groupRefs = useRef({});
  useEffect(() => {
    if (!openGroup) return undefined;
    const timer = setTimeout(() => {
      groupRefs.current[openGroup]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, 300);
    return () => clearTimeout(timer);
  }, [openGroup]);

  // Show a bottom fade only while there are more items below the visible area.
  const listRef = useRef(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const updateMoreBelow = () => {
    const el = listRef.current;
    if (!el) return;
    setHasMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  };
  useEffect(() => {
    updateMoreBelow();
    window.addEventListener("resize", updateMoreBelow);
    return () => window.removeEventListener("resize", updateMoreBelow);
  });

  const handleItemClick = ({ target, ctxKey, type }) => {
    // Settings pages don't touch the discovery/audit state
    if (target === "settings" || target === "templateSetting") return;
    if (ctxKey === "discovery") {
      discoveryCtxV2.updateType(type);
    }
    if (ctxKey === "audit") {
      brandAuditDiscoveryContext.updateType(type);
    }
    discoveryCtx.updateLanguage("en");
    discoveryCtxV2.updateLanguage("en");
    discoveryCtx.resetState();
    discoveryCtxV2.resetState();
    discoveryCtxV2.resetFormState(true);
    brandAuditDiscoveryContext.resetState();
    brandAuditDiscoveryContext.resetFormState(true);
    categoryAnalysisCtx.resetState();
    categoryAnalysisCtx.resetFormState(true);
  };

  const renderLink = (item, nested = false) => {
    const { id, target, label, icon: Icon, iconOutlined: IconOutlined } = item;
    return (
      <li className={styles.navItem} key={id ?? target}>
        <NavLink
          className={({ isActive }) =>
            `${styles.navLink} ${nested ? styles.navLinkNested : ""} ${
              (item.ctxKey ? isTypedItemActive(item) : isActive) ? styles.navLinkActive : ""
            }`
          }
          onClick={() => handleItemClick(item)}
          to={target}
        >
          {({ isActive: routeActive }) => {
            const isActive = item.ctxKey ? isTypedItemActive(item) : routeActive;
            return (
              <React.Fragment>
                {!nested && (
                  <span className={`${styles.iconWrap} ${isActive ? styles.iconWrapActive : ""}`}>
                    {isActive ? (
                      <Icon className={`${styles.activeIcon}`} />
                    ) : (
                      <IconOutlined className={`${styles.icon}`} />
                    )}
                  </span>
                )}
                <span className={`${styles.labelText}`}>{label}</span>
              </React.Fragment>
            );
          }}
        </NavLink>
      </li>
    );
  };

  const renderGroup = ({ key, label, icon: Icon, iconOutlined: IconOutlined, items, alwaysGroup }) => {
    // A group with a single item is not worth an expander: show it as a plain link.
    if (items.length === 1 && !alwaysGroup) return renderLink(items[0]);

    const isOpen = openGroup === key;
    const hasActive = activeGroupKey === key;
    // Collapsed group that holds the current page: highlight it like an active item and name the page,
    // so it is clear where you are while another group is open.
    const collapsedActive = hasActive && !isOpen;
    const activeChild = collapsedActive ? items.find(isItemActive) : null;
    return (
      <li className={styles.navItem} key={key} ref={(el) => { groupRefs.current[key] = el; }}>
        <button
          type="button"
          className={`${styles.navLink} ${styles.groupHeader} ${hasActive ? styles.groupHeaderActive : ""} ${
            collapsedActive ? styles.groupHeaderCollapsedActive : ""
          }`}
          aria-expanded={isOpen}
          onClick={() => setOpenGroup(isOpen ? null : key)}
        >
          <span className={`${styles.iconWrap} ${hasActive ? styles.iconWrapActive : ""}`}>
            {hasActive ? (
              <Icon className={`${styles.activeIcon}`} />
            ) : (
              <IconOutlined className={`${styles.icon}`} />
            )}
          </span>
          <span className={styles.labelWrap}>
            <span className={`${styles.labelText}`}>{label}</span>
            {activeChild && <span className={styles.currentPage}>{activeChild.label}</span>}
          </span>
          <ExpandMore className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} />
        </button>
        <div className={`${styles.groupBody} ${isOpen ? styles.groupBodyOpen : ""}`}>
          <ul className={styles.groupList}>{items.map((item) => renderLink(item, true))}</ul>
        </div>
      </li>
    );
  };

  return (
    <ul
      ref={listRef}
      className="nav nav-pills p-3 flex-column mb-auto"
      onScroll={updateMoreBelow}
      onTransitionEnd={updateMoreBelow}
    >
      {topLevelItems.map((item) => renderLink(item))}
      {groups.map(renderGroup)}
      <li
        aria-hidden="true"
        className={`${styles.scrollFade} ${hasMoreBelow ? styles.scrollFadeVisible : ""}`}
      />
    </ul>
  );
};

export default SidebarV2;
